import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Badge } from 'antd';
import {
  LayoutDashboard,
  Wallet,
  Users,
  TrendingUp,
  Headset,
  Contact,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  Link2,
  Target,
} from 'lucide-react';
import { cn } from '../../utils/tailwindUtils';
import { AuthContext } from '../../context/AuthContext';

const { Sider } = Layout;

const NAV_ITEMS = [
  { key: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
  { key: '/dashboard/trends', label: 'Trends', icon: TrendingUp, module: 'trends' },
  { key: '/finance', label: 'Finance', icon: Wallet, module: 'finance_budgets' },
  { key: '/hr', label: 'HR', icon: Users, module: 'hr_overview' },
  { key: '/sales', label: 'Sales', icon: TrendingUp, module: 'sales_overview' },
  { key: '/support', label: 'Development', icon: Headset, module: 'support_overview' },
  { key: '/people', label: 'People', icon: Contact, module: 'hr_overview' },
  { key: '/assignments', label: 'Assignments', icon: Target, module: 'assignments' },
  { key: '/alerts', label: 'Alerts', icon: Bell, module: 'alerts' },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    module: 'settings',
    children: [
      { key: '/settings/users', label: 'Users', module: 'users' },
      { key: '/settings/roles', label: 'Roles', module: 'users' },
      { key: '/settings/connect-crm', label: 'Connect CRM', icon: Link2, module: 'settings' },
      { key: '/settings/connect-hrms', label: 'Connect HRMS', icon: Link2, module: 'settings' },
      { key: '/settings/connect-fixdesk', label: 'Connect FixDesk', icon: Link2, module: 'settings' },
      { key: '/settings/targets', label: 'Targets', icon: Target, module: 'settings' },
    ],
  },
];

const Sidebar = ({ collapsed, onCollapse, mobileOpen, onMobileClose, healthScore }) => {
  const location = useLocation();
  const { hasPermission } = React.useContext(AuthContext);

  // Filter nav items by permission
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.module) return true;
    return hasPermission(item.module, 'view');
  }).map((item) => {
    if (item.children) {
      return {
        ...item,
        children: item.children.filter((child) => {
          if (!child.module) return true;
          return hasPermission(child.module, 'view');
        }),
      };
    }
    return item;
  }).filter((item) => !item.children || item.children.length > 0);

  // Determine which keys are selected/open
  const selectedKey = location.pathname;
  const openKey = visibleItems.find(
    (item) =>
      item.children &&
      item.children.some((child) => location.pathname.startsWith(child.key))
  )?.key;

  // Build antd menu items
  const menuItems = visibleItems.map((item) => {
    const Icon = item.icon;

    if (item.children) {
      return {
        key: item.key,
        icon: <Icon size={18} />,
        label: item.label,
        children: item.children.map((child) => ({
          key: child.key,
          label: <Link to={child.key}>{child.label}</Link>,
        })),
      };
    }

    return {
      key: item.key,
      icon: <Icon size={18} />,
      label: <Link to={item.key}>{item.label}</Link>,
    };
  });

  const siderContent = (
    <>
      {/* Logo area */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 h-16 border-b border-white/10',
          collapsed && 'justify-center px-2'
        )}
      >
        <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        {!collapsed && (
          <h1 className="text-lg font-bold text-white whitespace-nowrap">
            ERP Platform
          </h1>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2">
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKey ? [openKey] : []}
          items={menuItems}
          className="erp-sidebar-menu border-none"
          style={{ background: 'transparent' }}
        />
      </div>

      {/* Health score badge at bottom */}
      {healthScore !== undefined && healthScore !== null && (
        <div
          className={cn(
            'border-t border-white/10 p-4',
            collapsed && 'p-2 flex justify-center'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-3',
              collapsed && 'flex-col gap-1'
            )}
          >
            <Badge
              count={healthScore}
              overflowCount={100}
              style={{
                backgroundColor:
                  healthScore >= 75
                    ? '#22c55e'
                    : healthScore >= 50
                    ? '#eab308'
                    : '#ef4444',
                fontWeight: 600,
                fontSize: 12,
              }}
              showZero
            />
            {!collapsed && (
              <span className="text-xs text-white/70">Health Score</span>
            )}
          </div>
        </div>
      )}

      {/* Collapse toggle (desktop only) */}
      <div className="hidden md:block border-t border-white/10">
        <button
          onClick={() => onCollapse(!collapsed)}
          className="w-full flex items-center justify-center py-3 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-60 flex flex-col md:hidden transition-transform duration-300',
          'bg-[hsl(239,84%,30%)]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <button
          onClick={onMobileClose}
          className="absolute top-4 right-4 text-white/70 hover:text-white"
        >
          <X size={20} />
        </button>
        {siderContent}
      </div>

      {/* Desktop sidebar using Ant Sider */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        trigger={null}
        width={240}
        collapsedWidth={72}
        className="hidden md:flex md:flex-col"
        style={{
          background: 'hsl(var(--sidebar-background))',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 30,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {siderContent}
      </Sider>
    </>
  );
};

export default Sidebar;
